/**
 * Board-, Bestelllisten- und Tabellenkonstanten - 1:1-Portierung aus `index.html`
 * (Stand 2026-08-08). Zusammengefasst ist hier alles, was die Bestellansichten *zeichnet*:
 * die Board-Spalten samt Statussymbolik, die Spalten der Bestellliste, die Kopfzeilen der
 * Board- und Artikeltabelle und die Gruppierung des Boards. Alle diese Konstanten werden von
 * denselben Ansichten gelesen und aendern sich gemeinsam.
 *
 * Die Werte sind woertlich uebernommen: keine umsortierten Schluessel, keine vereinheitlichten
 * Schreibweisen, keine stillschweigend korrigierten Labels. Ergaenzt wurden ausschliesslich Typen -
 * der Typ beschreibt den Wert, er formt ihn nicht um.
 */

// ---- Statussymbole (index.html:4043-4053) ----

/** Die acht im Original vergebenen Symbolschluessel. */
export type StatusIconKey =
  | "clipboard"
  | "factory"
  | "ship"
  | "shield"
  | "warehouse"
  | "check"
  | "truck"
  | "list";

/**
 * Symbolschluessel -> Lucide-Iconname (index.html:4043-4052).
 *
 * ACHTUNG fuer aufrufenden Code: Das Original indiziert diese Tabelle mit einem Wert aus den Daten
 * (`STATUS_ICONS[column.icon]` in `setStatusDefinitions`, index.html:4149). Das ist ein Zugriff auf
 * ein Objekt mit `Object.prototype` in der Kette - ein Datensatz mit `icon: "constructor"` greift
 * durch. Die Tabelle bleibt hier bewusst ein gewoehnliches Objektliteral (jede andere Form waere
 * eine Wertaenderung).
 *
 * Der Durchgriff wird in dieser Phase NICHT abgesichert: ein `Object.hasOwn` in der lesenden
 * Funktion waere eine Verhaltensaenderung, und die Portierungsphase verlangt woertliche
 * Uebernahme. Die lesende Stelle (`ikonenTabelle` in `src/lib/status.ts:259-266`) loest die
 * Typeinengung, die der geschlossene Union-Typ `StatusIconKey` erzwingt, deshalb ueber einen Cast,
 * der zur Laufzeit exakt denselben Zugriff ausfuehrt. (Eine fruehere Fassung dieses Kommentars hat
 * hier `Object.hasOwn` angewiesen - das ist ueberholt, der Code ist massgeblich.)
 */
export const STATUS_ICONS: Readonly<Record<StatusIconKey, string>> = {
  clipboard: "clipboard-pen",
  factory: "factory",
  ship: "ship",
  shield: "shield-check",
  warehouse: "warehouse",
  check: "circle-check-big",
  truck: "split",
  list: "list",
};

/** Symbolschluessel -> deutsche Beschriftung (index.html:4053). */
export const STATUS_ICON_LABELS: Readonly<Record<StatusIconKey, string>> = {
  clipboard: "Vorbereitung",
  factory: "Produktion",
  ship: "Transport",
  shield: "Prüfung",
  warehouse: "Lager",
  check: "Freigabe",
  truck: "Anlieferung",
  list: "Allgemein",
};

// ---- Board-Spalten (index.html:4054-4063) ----

export interface BoardColumn {
  id: string;
  label: string;
  /** Sechsstelliger Hex-Farbwert mit fuehrendem `#`. */
  color: string;
  icon: StatusIconKey;
  /** Anzeigereihenfolge auf dem Board. */
  position: number;
  /** Fortschrittsstufe 0-7 auf dem Transport-Zeitstrahl. */
  stage: number;
}

/**
 * Auslieferungszustand der Board-Spalten (index.html:4054-4063). Die Reihenfolge ist fachlich
 * tragend - `position` gibt sie zusaetzlich explizit an, beide stimmen im Original ueberein.
 *
 * Abweichend von der sonst woertlichen 1:1-Portierung (siehe Kopfkommentar der Datei): die
 * frueheren Spalten "Split" und "Sammellieferung" sind entfernt (bewusste Statusbereinigung,
 * nicht Teil der Portierung). Beide Schreibpfade setzen seither bestehende Statuswerte
 * ("Vorbereiten"/"Abgeschlossen", siehe `backend/src/jtl/schreiben/sammellieferung.ts`) statt
 * dieser eigenen Spalten.
 */
export const DEFAULT_BOARD_COLUMNS: readonly BoardColumn[] = [
  { id: "vorbereiten", label: "Vorbereiten", color: "#8a63d2", icon: "clipboard", position: 0, stage: 0 },
  { id: "produktion", label: "Produktion", color: "#d99a26", icon: "factory", position: 1, stage: 0 },
  { id: "import", label: "Import", color: "#4a7de0", icon: "ship", position: 2, stage: 3 },
  { id: "zoll", label: "Zoll", color: "#2ba88a", icon: "shield", position: 3, stage: 5 },
  { id: "angeliefert", label: "Angeliefert", color: "#7d9614", icon: "warehouse", position: 4, stage: 7 },
  { id: "abgeschlossen", label: "Abgeschlossen", color: "#27845f", icon: "check", position: 5, stage: 7 },
];

/**
 * Alte Status-IDs (vor dem Umbau 2026-07) auf die neuen abbilden (index.html:4165-4166, Kommentar
 * woertlich uebernommen).
 */
export const LEGACY_STATUS: Readonly<Record<string, string>> = {
  see: "import",
  hafen: "zoll",
  lager: "angeliefert",
};

// ---- Bestellliste (index.html:4101-4122) ----

/**
 * Spaltenschluessel, die es in der Bestellliste einmal gab und die im Original beim Laden alter
 * Einstellungen verworfen werden (index.html:4101-4106). Ein `Set`, wie im Original -
 * `normalizeOrderListColumns` fragt es mit `.has(...)` ab. Seit dem 2026-08-25 tut das auch diese
 * SPA: `state/Speicher.tsx` ruft `normalizeOrderListColumns` (`lib/status.ts:222`) im Ladepfad
 * (Zweig 1 von `bestandLaden`), genau wie das Original in `normalizedData` (index.html:5882).
 */
export const REMOVED_ORDER_LIST_KEYS: ReadonlySet<string> = new Set([
  "tracking",
  "invoice",
  "p1amount",
  "p1due",
  "p1status",
  "p2amount",
  "p2due",
  "p2status",
  "paymentStatus",
  "terms",
]);

export interface OrderListColumn {
  id: string;
  key: string;
  label: string;
  position: number;
  visible: boolean;
  /** `true` bei nachtraeglich vom Benutzer angelegten Spalten. */
  custom: boolean;
}

/**
 * Auslieferungszustand der Bestelllisten-Spalten (index.html:4107-4111).
 *
 * Die Ableitung aus den `[key, label]`-Paaren ist woertlich aus dem Original uebernommen statt das
 * Ergebnis auszuschreiben: so bleibt sichtbar, dass `id` und `key` per Konstruktion identisch sind
 * und `position` aus der Reihenfolge stammt.
 *
 * "payment" (Auftrag "Zahlung anstoßen Knopf + Einfügung ... in der Übersicht", Asana Task
 * 1218171668075282, 2026-09-04): KEIN Bestandsvorbild - `paymentInitiatedAt` existierte im Original
 * nicht als Bestellboard-Spalte, nur als Feld im Zahlung-Tab. Deshalb hier zwischen "eta" und
 * "booked" eingefuegt (Mockup des Tickets), nicht aus `index.html` uebernommen - siehe
 * `KONSTANTEN_NAMEN_ABWEICHEND` (test/helpers/configKonstanten.ts).
 *
 * "zoll" (Spec Zollinstruktionen im Hub, 2026-10-31): ebenfalls KEIN Bestandsvorbild. Steht zwischen
 * "eta" und "payment"; die Zelle zeigt die Plakette "Zollinstruktionen angestoßen", sobald der Task
 * "Zollinstruktionen an Spedition senden (Outlook)" erledigt ist (`lib/zollinstruktionen.ts`).
 */
export const DEFAULT_ORDER_LIST_COLUMNS: readonly OrderListColumn[] = (
  [
    ["name", "Bestellnummer"],
    ["dsvShipmentNo", "Trackingnummer"],
    ["due", "Fälligkeitsdatum"],
    ["supplier", "Lieferant"],
    ["status", "Status"],
    ["invoiceNo", "Rechnungsnummer"],
    ["value", "Bestellwert"],
    ["etdPi", "PI - ETD"],
    ["etd", "ETD"],
    ["eta", "ETA"],
    ["zoll", "Zollinstruktionen anstoßen"],
    ["payment", "Zahlung anstoßen"],
    ["booked", "Ware eingebucht"],
  ] as const
).map(([key, label], position) => ({ id: key, key, label, position, visible: true, custom: false }));

export interface ColumnSize {
  width: number;
  min: number;
  max: number;
}

/** Spaltenbreiten der Bestellliste in Pixeln (index.html:4112-4122). */
export const ORDER_LIST_COLUMN_SIZES: Readonly<Record<string, ColumnSize>> = {
  name: { width: 190, min: 100, max: 560 },
  dsvShipmentNo: { width: 140, min: 100, max: 260 },
  due: { width: 132, min: 90, max: 260 },
  supplier: { width: 220, min: 100, max: 520 },
  status: { width: 150, min: 100, max: 340 },
  invoiceNo: { width: 150, min: 100, max: 300 },
  value: { width: 130, min: 90, max: 280 },
  etdPi: { width: 118, min: 88, max: 240 },
  etd: { width: 118, min: 88, max: 240 },
  eta: { width: 118, min: 88, max: 240 },
  zoll: { width: 165, min: 105, max: 320 },
  payment: { width: 165, min: 105, max: 320 },
  // Auftrag "Ware eingebucht mit Datum" (2026-10-20): Original 140. Die Pille traegt seither Zustand
  // UND Datum ("vollständig · 12. Sept. 2026", `EingebuchtPille`) und passt in 140 nicht hinein.
  // Gilt nur fuer Nutzer ohne gespeicherte Breite - eine bereits gezogene Breite bleibt unberuehrt.
  booked: { width: 225, min: 96, max: 300 },
};

/**
 * Rueckfallgroesse fuer Spalten, die NICHT in `ORDER_LIST_COLUMN_SIZES` stehen (index.html:4123) -
 * also fuer die vom Benutzer selbst angelegten (`custom: true`). `orderListColumnSize`
 * (`lib/orderList.ts`) liest sie als einzige Stelle.
 *
 * Nachgetragen in Task 13 (Phase 6): Phase 2b hat die Nachbarkonstanten uebernommen, diese aber
 * nicht - ohne sie waere jede eigene Spalte breitenlos.
 */
export const DEFAULT_CUSTOM_ORDER_LIST_COLUMN_SIZE: ColumnSize = { width: 150, min: 90, max: 520 };

// ---- Board-Gruppierung und Tabellenkoepfe (index.html:4607-4632) ----

export interface BoardGroup {
  id: string;
  label: string;
}

/**
 * Gruppen, in die das Board seine Bestellungen einsortiert (index.html:4607-4613).
 *
 * Die Gruppen "completed" ("Abgeschlossen") und "split" ("Split", vormals "Sammellieferung /
 * Split") sind seit Task 1218053584680825 (2026-09-01, "Bereich Abgeschlossen, Sammel und Split
 * auf der Bestellübersicht weg") keine eigenen Board-Bereiche mehr. `boardGroupId`
 * (`lib/status.ts`) ordnet beide Faelle stattdessen "open" zu: vollstaendige Bestellungen
 * (`isComplete`) verlassen den aktiven Bestand ueber einen automatischen Archivierungs-Sweep
 * (`App.tsx`) statt in einer eigenen Spalte zu erscheinen; Teillieferungen
 * (`isSplitCollectionOrder`) laufen als gewoehnliche offene Bestellungen mit. Die weiterhin
 * bestehenden Gruppen "waiting-payment" und "waiting-docs" ("Abgeschlossen – Warten auf ...")
 * sind von dieser Aenderung NICHT betroffen.
 */
export const BOARD_GROUPS: readonly BoardGroup[] = [
  { id: "open", label: "Offen" },
  { id: "waiting-payment", label: "Abgeschlossen – Warten auf Zahlung" },
  { id: "waiting-docs", label: "Abgeschlossen – Warten auf Dokumente" },
];

export interface TableColumn extends ColumnSize {
  key: string;
  label: string;
  /** Nur bei Zahlenspalten gesetzt (rechtsbuendig). */
  numeric?: boolean;
}

/** Spalten der Bestelltabelle im Board (index.html:4614-4624). */
export const ORDER_BOARD_COLUMNS: readonly TableColumn[] = [
  { key: "order", label: "Bestellung", width: 310, min: 230, max: 560 },
  { key: "supplier", label: "Lieferant", width: 250, min: 180, max: 440 },
  { key: "status", label: "Status", width: 170, min: 130, max: 300 },
  { key: "etd", label: "ETD", width: 132, min: 112, max: 220 },
  { key: "eta", label: "ETA", width: 132, min: 112, max: 220 },
  { key: "payment", label: "Zahlung", width: 170, min: 135, max: 280 },
  { key: "docs", label: "Dokumente", width: 170, min: 140, max: 280 },
  { key: "tasks", label: "Tasks", width: 100, min: 82, max: 180 },
  { key: "value", label: "Bestellwert", width: 150, min: 120, max: 240 },
];

/** Spalten der Artikeltabelle im Bestell-Drawer (index.html:4625-4632). */
export const ITEM_TABLE_COLUMNS: readonly TableColumn[] = [
  { key: "article", label: "Artikel", width: 180, min: 110, max: 520 },
  { key: "sku", label: "SKU", width: 84, min: 64, max: 280 },
  { key: "qty", label: "Menge", width: 66, min: 58, max: 180, numeric: true },
  { key: "price", label: "EK / Stk.", width: 84, min: 68, max: 200, numeric: true },
  { key: "sum", label: "Summe", width: 78, min: 68, max: 220, numeric: true },
  { key: "type", label: "Typ", width: 92, min: 70, max: 190 },
];

/**
 * Schwelle fuer "hoher Bestellwert" in Euro (index.html:4660).
 *
 * ANMERKUNG: Die Konstante wird im gesamten Original an keiner Stelle gelesen (einziges Vorkommen
 * ist ihre Definition). Sie steht laut Task-Brief auf der Portierungsliste und ist deshalb hier
 * uebernommen; die Einordnung in dieses Modul folgt der Bestellwert-Spalte (`value`) der Board- und
 * Bestelllistenansicht. Ohne Nutzung im Original gibt es dafuer keinen staerkeren Beleg.
 */
export const HIGH_VALUE_LIMIT = 50000;
